import React, { useMemo, useState, useEffect } from 'react';

const CodeBlock = ({ code, language = 'json' }: { code: string, language?: string }) => (
  <div className="bg-gray-900 rounded-xl p-4 my-4 font-mono text-sm border border-gray-800 relative group overflow-x-auto">
    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button 
        onClick={() => {
          navigator.clipboard.writeText(code);
          alert('Copied to clipboard!');
        }}
        className="bg-gray-800 hover:bg-gray-700 text-gray-300 p-2 rounded-lg text-xs"
      >
        <i className="fas fa-copy"></i>
      </button>
    </div>
    <pre className="text-emerald-400">
      <code>{code}</code>
    </pre>
  </div>
);

const EndpointBadge = ({ method }: { method: string }) => {
  const colors: any = {
    'GET': 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    'POST': 'bg-green-500/20 text-green-400 border-green-500/50',
    'PUT': 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    'PATCH': 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    'DELETE': 'bg-red-500/20 text-red-400 border-red-500/50',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold border mr-3 ${colors[method] || 'bg-gray-500/20 text-gray-400'}`}>
      {method}
    </span>
  );
};

const DeveloperPortal: React.FC = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = useMemo(() => ([
    { id: 'getting-started', label: 'Getting Started', icon: 'fas fa-rocket' },
    { id: 'authentication', label: 'Authentication', icon: 'fas fa-lock' },
    { id: 'service-forms', label: 'Service Forms', icon: 'fas fa-file-alt' },
    { id: 'bulk-form-import', label: 'Bulk Form Import', icon: 'fas fa-file-excel' },
    { id: 'packages', label: 'Packages', icon: 'fas fa-box' },
    { id: 'users', label: 'Users', icon: 'fas fa-users' },
    { id: 'impact-plan', label: 'Impact Plan', icon: 'fas fa-project-diagram' },
    { id: 'monitor', label: 'Starlink Monitor', icon: 'fas fa-satellite' },
    { id: 'power-bi', label: 'Power BI', icon: 'fas fa-chart-line' },
    { id: 'activity', label: 'Activity Logs', icon: 'fas fa-history' },
    { id: 'service-accounts-mgmt', label: 'Account Management', icon: 'fas fa-robot' },
    { id: 'rate-limiting', label: 'Rate Limiting', icon: 'fas fa-tachometer-alt' },
    { id: 'pagination', label: 'Pagination', icon: 'fas fa-list-ol' },
    { id: 'errors', label: 'Error Codes', icon: 'fas fa-exclamation-triangle' },
  ]), []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition && element.offsetTop + element.offsetHeight > scrollPosition) {
          setActiveSection(section.id);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-300 font-sans selection:bg-indigo-500 selection:text-white">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-black/80 backdrop-blur-md border-b border-gray-800 z-50 px-8 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img 
            src="/logo.png" 
            alt="Aura Group Logo" 
            className="h-10 w-auto"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/assets/images/aura-group-logo.png';
            }}
          />
          <span className="text-xl font-bold text-white tracking-tight">Developer Portal</span>
        </div>
        <div className="flex items-center space-x-6">
          <a 
            href="/docs" 
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg shadow-indigo-500/20 flex items-center"
          >
            <i className="fas fa-book-open mr-2"></i>
            Swagger Docs
          </a>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sticky Sidebar */}
        <aside className="fixed left-0 top-16 bottom-0 w-72 bg-black border-r border-gray-800 overflow-y-auto hidden lg:block p-6">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollTo(section.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                  activeSection === section.id 
                    ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/30' 
                    : 'text-gray-500 hover:text-gray-300 hover:bg-gray-900'
                }`}
              >
                <i className={`${section.icon} w-5 text-center ${activeSection === section.id ? 'text-indigo-400' : 'group-hover:text-gray-300'}`}></i>
                <span className="font-medium text-sm">{section.label}</span>
              </button>
            ))}
          </nav>
          
          <div className="mt-12 p-4 bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl">
            <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">Master Developer</p>
            <p className="text-white font-bold tracking-tight">Aura Group Team</p>
            <div className="flex space-x-2 mt-3">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-[10px] text-gray-400">System Online</span>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-72 p-8 lg:p-16 max-w-5xl mx-auto">
          {/* Welcome Section */}
          <section id="getting-started" className="mb-24 scroll-mt-24">
            <h1 className="text-5xl font-extrabold text-white mb-6 tracking-tight">
              Welcome to the Aura Group Data Management System API
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed mb-8">
              Hello, developer! We are thrilled to have you here. This documentation is your complete guide to integrating with the Aura Group Data Management System. Whether you are building a mobile app, a data dashboard, or an automated sync service, we've designed our API to be robust, secure, and incredibly easy to use.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-gray-900/50 border border-gray-800 p-6 rounded-2xl">
                <h3 className="text-white font-bold mb-2 flex items-center">
                  <i className="fas fa-bolt text-yellow-500 mr-2"></i>
                  Fast Integration
                </h3>
                <p className="text-sm text-gray-500">JSON-based responses and standardized error handling for rapid development.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 p-6 rounded-2xl">
                <h3 className="text-white font-bold mb-2 flex items-center">
                  <i className="fas fa-shield-alt text-indigo-500 mr-2"></i>
                  Secure Access
                </h3>
                <p className="text-sm text-gray-500">Industry-standard JWT authentication with dedicated service account credentials.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-4">How it works</h2>
            <p className="text-gray-400 leading-relaxed">
              Our system acts as a central hub for Starlink usage tracking, Power BI synchronization, and customer service forms. By using our API, you gain programmatic access to all these features. All responses are served in <span className="text-white font-semibold">JSON format</span>, ensuring compatibility with virtually any modern programming language or framework.
            </p>
          </section>

          {/* Authentication */}
          <section id="authentication" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                <i className="fas fa-lock text-indigo-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Authentication</h2>
            </div>
            <p className="text-gray-400 mb-6">
              Security is our top priority. We use service accounts to provide dedicated API access. Once your administrator creates a service account, you will receive a <strong>Client ID</strong> and a <strong>Client Secret</strong>. Use these to obtain an access token.
            </p>
            
            <div className="space-y-8">
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/service-accounts/authenticate
                </h4>
                <p className="text-sm text-gray-500 mb-4">Send your credentials to receive a 15-minute access token.</p>
                <CodeBlock 
                  code={`// Request Body\n{\n  "clientId": "sa_your_client_id_here",\n  "clientSecret": "your_secret_here"\n}`} 
                />
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "access_token": "eyJhbGciOiJIUzI1NiIs...",\n  "token_type": "Bearer",\n  "expires_in": 900\n}`} 
                />
              </div>

              <div className="bg-indigo-900/20 border-l-4 border-indigo-500 p-6 rounded-r-2xl">
                <h4 className="text-indigo-400 font-bold mb-2">Pro Tip: Auto-Refresh</h4>
                <p className="text-sm text-indigo-300/80">
                  Tokens expire every 15 minutes. We recommend implementing a pre-request script or middleware in your application that automatically refreshes the token 1 minute before it expires.
                </p>
              </div>
            </div>
          </section>

          {/* Service Forms */}
          <section id="service-forms" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-emerald-600/20 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                <i className="fas fa-file-alt text-emerald-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Service Forms</h2>
            </div>
            <p className="text-gray-400 mb-8">
              The core of our system. Manage customer records, service lines, and usage tracking data.
            </p>

            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/forms
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all service forms with optional filtering and search.</p>
                <ul className="text-sm text-gray-400 space-y-2 mb-4">
                  <li><span className="text-white font-mono">search</span> (query): Search by name, ID, or kit number</li>
                  <li><span className="text-white font-mono">threshold</span> (query): Filter by usage percentage (e.g., 75)</li>
                  <li><span className="text-white font-mono">page/limit</span> (query): Pagination control (max 100 per request)</li>
                </ul>
              </div>

              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/forms
                </h4>
                <p className="text-sm text-gray-500 mb-4">Create a new customer record. The service line must not already exist.</p>
                <ul className="text-sm text-gray-400 space-y-2 mb-4">
                  <li><span className="text-white font-mono">customerName</span>, <span className="text-white font-mono">customerId</span>, <span className="text-white font-mono">serviceLine</span>, <span className="text-white font-mono">packageLimit</span>, <span className="text-white font-mono">activationDate</span> and <span className="text-white font-mono">isCapped</span> are all <span className="text-white font-semibold">required</span>.</li>
                  <li><span className="text-white font-mono">kitNumber</span>, <span className="text-white font-mono">oldServiceLine</span>, <span className="text-white font-mono">oldPackage</span>, <span className="text-white font-mono">dataTopup</span>, <span className="text-white font-mono">isActive</span> and <span className="text-white font-mono">accountManager</span> are optional.</li>
                </ul>
                <CodeBlock code={`{\n  "customerName": "Acme Corp",\n  "customerId": "CUST123",\n  "serviceLine": "1234567890",\n  "packageLimit": 100.5,\n  "activationDate": "2026-01-15",\n  "isCapped": true\n}`} />
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mt-4">
                  <h5 className="text-amber-400 font-bold text-sm mb-1">Calling this as a service account?</h5>
                  <p className="text-sm text-amber-300/80">
                    Also include a <span className="text-white font-mono">submittedByEmail</span> field with the email of the human user the form should be attributed to. Service accounts have no logged-in user of their own, so this is <span className="text-white font-semibold">required</span> when the request is authenticated with a service account token — the request is rejected with <span className="text-white font-mono">400</span> if it's missing. Any email is accepted and is always recorded on the form as-is — no DataPool Monitor account is ever created for it. If it happens to match an existing user, that user becomes the form's owner; otherwise the form is simply left with no linked owner account. Regular user logins can omit it; it's ignored in that case.
                  </p>
                  <CodeBlock code={`{\n  "customerName": "Acme Corp",\n  "customerId": "CUST123",\n  "serviceLine": "1234567890",\n  "packageLimit": 100.5,\n  "activationDate": "2026-01-15",\n  "isCapped": true,\n  "submittedByEmail": "user@example.com"\n}`} />
                </div>
                <p className="text-sm text-gray-500 mt-4">Returns the created <span className="text-white font-mono">201</span> form. Records an audit entry per field and invalidates the forms list cache.</p>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="PUT" />
                  /api/forms/:id
                </h4>
                <p className="text-sm text-gray-500 mb-4">Update an existing record by its ID.</p>
              </div>
            </div>
          </section>

          {/* Bulk Form Import */}
          <section id="bulk-form-import" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-emerald-600/20 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                <i className="fas fa-file-excel text-emerald-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Bulk Form Import</h2>
            </div>
            <p className="text-gray-400 mb-8">
              Create many service forms at once from a spreadsheet. This is a two-step flow: upload and validate a sheet with <span className="text-white font-mono">preview</span>, then submit the corrected rows to <span className="text-white font-mono">commit</span>. Nothing is created during preview.
            </p>

            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/forms/bulk-import/preview
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Upload an Excel workbook (<span className="text-white font-mono">multipart/form-data</span>, field name <span className="text-white font-mono">file</span>, max 5MB, first sheet only) and get back every row parsed and validated, without creating anything. Recognized columns include Customer Name, Customer ID, Service Line, Kit Number, Old Service Line, Package Limit, Data Topup, Old Package, Activation Date, Account Manager and Client Status.
                </p>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                  <h5 className="text-blue-400 font-bold text-sm mb-1">formType field</h5>
                  <p className="text-sm text-blue-300/80">
                    Include a <span className="text-white font-mono">formType</span> field alongside the file: <span className="text-white font-mono">"transfer"</span> (default) or <span className="text-white font-mono">"regular"</span>. Transfer rows require Old Service Line and Old Package (&gt; 0) and are created Uncapped; Regular rows don't need either field and are created Capped. The chosen type is echoed back in the preview response and must be sent again to <span className="text-white font-mono">commit</span> — rows are re-validated there against whichever type you pass.
                  </p>
                </div>
                <p className="text-sm text-gray-500 mb-2">Response:</p>
                <CodeBlock code={`{\n  "formType": "transfer",\n  "sheetName": "Sheet1",\n  "totalRows": 3,\n  "validRows": 2,\n  "invalidRows": 1,\n  "rows": [\n    {\n      "rowNumber": 2,\n      "data": {\n        "customerName": "Acme Corp",\n        "customerId": "CUST123",\n        "serviceLine": "1234567890",\n        "packageLimit": 100.5,\n        "dataTopup": 0,\n        "oldPackage": 50,\n        "activationDate": "2026-01-15",\n        "isActive": true,\n        "isCapped": false\n      },\n      "errors": []\n    },\n    {\n      "rowNumber": 3,\n      "data": { "...": "..." },\n      "errors": ["Service Line already exists"]\n    }\n  ]\n}`} />
                <p className="text-sm text-gray-500 mt-4">
                  Rows with a non-empty <span className="text-white font-mono">errors</span> array (missing required fields, invalid/duplicate service lines, non-positive limits, etc.) will be rejected by <span className="text-white font-mono">commit</span> — fix them in the sheet, or drop them, before committing.
                </p>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/forms/bulk-import/commit
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Creates a form for each row supplied. Send back the <span className="text-white font-mono">data</span> objects from a prior <span className="text-white font-mono">preview</span> call (JSON body this time, not multipart) under a <span className="text-white font-mono">rows</span> array, along with the same <span className="text-white font-mono">formType</span> ("transfer" or "regular", default "transfer") used for that preview. All rows are created in a single transaction — if the batch still contains any invalid row, nothing is created and the invalid rows are returned instead.
                </p>
                <CodeBlock code={`{\n  "formType": "transfer",\n  "rows": [\n    {\n      "customerName": "Acme Corp",\n      "customerId": "CUST123",\n      "serviceLine": "1234567890",\n      "packageLimit": 100.5,\n      "dataTopup": 0,\n      "oldPackage": 50,\n      "activationDate": "2026-01-15",\n      "isActive": true\n    }\n  ],\n  "submittedByEmail": "user@example.com"\n}`} />
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mt-4">
                  <h5 className="text-amber-400 font-bold text-sm mb-1">Calling this as a service account?</h5>
                  <p className="text-sm text-amber-300/80">
                    Just like <span className="text-white font-mono">POST /api/forms</span>, a top-level <span className="text-white font-mono">submittedByEmail</span> is <span className="text-white font-semibold">required</span> so every form in the batch records who it came from — <span className="text-white font-mono">400</span> if missing. Any email is accepted and no DataPool Monitor account is ever created for it; it's used as the owner only if it happens to already match an existing user. Regular user logins can omit it.
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  On success, returns <span className="text-white font-mono">201</span> with a count and the created forms:
                </p>
                <CodeBlock code={`{\n  "message": "2 transfer forms imported successfully",\n  "createdCount": 2,\n  "forms": [ { "...": "created ServiceForm" } ]\n}`} />
              </div>
            </div>
          </section>

          {/* Packages */}
          <section id="packages" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-blue-600/20 rounded-2xl flex items-center justify-center border border-blue-500/30">
                <i className="fas fa-box text-blue-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Packages</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Manage data packages and caps. Packages define the baseline data limits for service forms and customer kits.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/packages
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all active data packages with pagination.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "data": [\n    {\n      "id": 1,\n      "capGB": 50,\n      "active": true,\n      "createdAt": "2026-01-01T10:00:00Z"\n    }\n  ],\n  "pagination": {\n    "page": 1,\n    "limit": 50,\n    "total": 12,\n    "totalPages": 1\n  }\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/packages
                </h4>
                <p className="text-sm text-gray-500 mb-4">Create a new data package definition.</p>
                <CodeBlock 
                  code={`// Request Body\n{\n  "capGB": 100\n}`} 
                />
                <CodeBlock 
                  code={`// Success Response (201 Created)\n{\n  "id": 2,\n  "capGB": 100,\n  "active": true,\n  "createdAt": "2026-01-13T14:30:00Z"\n}`} 
                />
              </div>
            </div>
          </section>

          {/* Users */}
          <section id="users" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-purple-600/20 rounded-2xl flex items-center justify-center border border-purple-500/30">
                <i className="fas fa-users text-purple-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Users</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              System user management. Service accounts have full access to these endpoints to manage the administrative side of the platform, including role assignments and account status.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/auth/users
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all system users with detailed role information.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n[\n  {\n    "id": 1,\n    "email": "admin@auragrp.com",\n    "username": "admin",\n    "role": "admin",\n    "isActive": true,\n    "isAdmin": true,\n    "isSuperAdmin": false\n  }\n]`} 
                />
              </div>

              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/auth/users
                </h4>
                <p className="text-sm text-gray-500 mb-4">Create a new system user. An invitation email will be sent automatically.</p>
                <CodeBlock 
                  code={`// Request Body\n{\n  "email": "dev@auragrp.com",\n  "username": "developer",\n  "role": "user",\n  "isAdmin": false\n}`} 
                />
                <CodeBlock 
                  code={`// Success Response (201 Created)\n{\n  "id": 15,\n  "email": "dev@auragrp.com",\n  "username": "developer",\n  "emailSent": true\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/auth/users/me
                </h4>
                <p className="text-sm text-gray-500 mb-4">Get details of the currently authenticated identity (Service Account or User).</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "id": 1,\n  "email": "api-service@internal",\n  "role": "admin",\n  "hasAdminAccess": true\n}`} 
                />
              </div>
            </div>
          </section>

          {/* Impact Plan */}
          <section id="impact-plan" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-pink-600/20 rounded-2xl flex items-center justify-center border border-pink-500/30">
                <i className="fas fa-project-diagram text-pink-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Impact Plan</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Manage the exclusive Impact Plan customers and monitor their specific usage patterns. These endpoints deal with high-priority service lines.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/impactplan/accounts
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all Impact Plan customer accounts with real-time usage metrics.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n[\n  {\n    "service_line_number": "1234567890",\n    "customer_name": "Impact Customer A",\n    "current_usage_gb": 450.2,\n    "usage_percentage": 9.00,\n    "package_limit_gb": 5000\n  }\n]`} 
                />
              </div>

              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/impactplan/control/trigger
                </h4>
                <p className="text-sm text-gray-500 mb-4">Manually trigger an immediate monitoring cycle for all Impact Plan lines.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "message": "Impact Plan monitor cycle completed successfully"\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/impactplan-100-percent
                </h4>
                <p className="text-sm text-gray-500 mb-4">Access historical records of service lines that reached 100% usage.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "data": [\n    {\n      "id": 5,\n      "serviceLine": "9876543210",\n      "customerName": "Exceeded Customer",\n      "reachedAt": "2026-01-12T08:00:00Z"\n    }\n  ]\n}`} 
                />
              </div>
            </div>
          </section>

          {/* Monitor */}
          <section id="monitor" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-teal-600/20 rounded-2xl flex items-center justify-center border border-teal-500/30">
                <i className="fas fa-satellite text-teal-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Starlink Monitor</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Interact with the Starlink monitoring service, check real-time status, and manage the automated tracking engine that collects data from the Starlink API.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/monitor/status
                </h4>
                <p className="text-sm text-gray-500 mb-4">Get the current operational status of the monitoring service.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "running": true,\n  "logFile": "/logs/starlink-monitor.log",\n  "timestamp": "2026-01-13T14:45:00Z"\n}`} 
                />
              </div>

              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/monitor/usage/aggregate/current
                </h4>
                <p className="text-sm text-gray-500 mb-4">Fetch the latest aggregated usage data for the current month.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "year": 2026,\n  "month": 1,\n  "totalPriorityDataGB": 15420.5,\n  "totalServiceLines": 150,\n  "emailStatus": "sent"\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/monitor/logs
                </h4>
                <p className="text-sm text-gray-500 mb-4">Retrieve the latest operational logs from the monitoring engine.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n[\n  "2026-01-13 14:40:01 CAT - starlink-monitor - INFO - Starting usage monitoring cycle",\n  "2026-01-13 14:40:05 CAT - starlink-monitor - INFO - Processing service line 1234567890..."\n]`} 
                />
              </div>
            </div>
          </section>

          {/* Power BI */}
          <section id="power-bi" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-yellow-600/20 rounded-2xl flex items-center justify-center border border-yellow-500/30">
                <i className="fas fa-chart-line text-yellow-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Power BI Sync</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Manage data synchronization between the system and Power BI dashboards. This ensures your visual reports always have the latest data.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="POST" />
                  /api/powerbi/run
                </h4>
                <p className="text-sm text-gray-500 mb-4">Manually initiate an immediate data synchronization pass.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "success": true,\n  "processedTables": 12,\n  "durationSeconds": 45\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/powerbi/data/tables
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all database tables available for synchronization with Power BI.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "tables": [\n    {\n      "table_name": "main_data_usage",\n      "column_count": 8,\n      "estimated_rows": 154200\n    }\n  ]\n}`} 
                />
              </div>
            </div>
          </section>

          {/* Activity Logs */}
          <section id="activity" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyan-600/20 rounded-2xl flex items-center justify-center border border-cyan-500/30">
                <i className="fas fa-history text-cyan-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Activity Logs</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Track every action performed within the system for full auditability and transparency. Every programmatic or manual change is recorded here.
            </p>
            <div className="space-y-8">
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/activity
                </h4>
                <p className="text-sm text-gray-500 mb-4">Retrieve the 20 most recent system activities.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n[\n  {\n    "id": 1024,\n    "type": "FORM_UPDATE",\n    "description": "Updated usage for line 1234567890",\n    "user": "starlink-monitor",\n    "timestamp": "2026-01-13T14:40:05Z"\n  }\n]`} 
                />
              </div>
            </div>
          </section>

          {/* Service Accounts Management */}
          <section id="service-accounts-mgmt" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gray-600/20 rounded-2xl flex items-center justify-center border border-gray-500/30">
                <i className="fas fa-robot text-gray-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Account Management</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Manage API keys and service accounts programmatically. These endpoints are restricted to Administrator access.
            </p>
            <div className="space-y-12">
              <div className="border-b border-gray-800 pb-8">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="GET" />
                  /api/service-accounts
                </h4>
                <p className="text-sm text-gray-500 mb-4">List all configured service accounts and their usage statistics.</p>
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "data": [\n    {\n      "id": 1,\n      "clientId": "sa_5f3e...",\n      "name": "Production Dashboard",\n      "isActive": true,\n      "totalRequests": 1542\n    }\n  ],\n  "pagination": { ... }\n}`} 
                />
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <EndpointBadge method="PATCH" />
                  /api/service-accounts/:id
                </h4>
                <p className="text-sm text-gray-500 mb-4">Update a service account's name, description, or active status.</p>
                <CodeBlock 
                  code={`// Request Body\n{\n  "isActive": false\n}`} 
                />
                <CodeBlock 
                  code={`// Success Response (200 OK)\n{\n  "id": 1,\n  "isActive": false,\n  "updatedAt": "2026-01-13T14:50:00Z"\n}`} 
                />
              </div>
            </div>
          </section>

          {/* Pagination */}
          <section id="pagination" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-orange-600/20 rounded-2xl flex items-center justify-center border border-orange-500/30">
                <i className="fas fa-list-ol text-orange-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Pagination</h2>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              To ensure optimal performance and low latency, we enforce a strict <span className="text-white font-bold underline decoration-orange-500">maximum of 100 items per request</span> for all list endpoints. If you need 1000 items, you must make 10 requests.
            </p>
            <CodeBlock code={`// Paginated Response Structure\n{\n  "data": [...], // Max 100 items\n  "pagination": {\n    "page": 1,\n    "limit": 100,\n    "total": 1523,\n    "totalPages": 16,\n    "hasNext": true,\n    "hasPrev": false\n  }\n}`} />
          </section>

          {/* Errors */}
          <section id="errors" className="mb-24 scroll-mt-24">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-red-600/20 rounded-2xl flex items-center justify-center border border-red-500/30">
                <i className="fas fa-exclamation-triangle text-red-400 text-xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-white tracking-tight">Error Codes</h2>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Our API uses standard HTTP response codes to indicate the success or failure of an API request.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-white font-bold mb-1">200 OK</p>
                <p className="text-xs text-gray-500">The request was successful.</p>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-white font-bold mb-1">401 Unauthorized</p>
                <p className="text-xs text-gray-500">Invalid or expired token provided.</p>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-white font-bold mb-1">403 Forbidden</p>
                <p className="text-xs text-gray-500">Service account is disabled or missing permissions.</p>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-white font-bold mb-1">429 Too Many Requests</p>
                <p className="text-xs text-gray-500">Rate limit exceeded (100 req/15min).</p>
              </div>
            </div>
          </section>

          {/* Final Message */}
          <section className="mt-32 pt-16 border-t border-gray-800 text-center">
            <div className="inline-block w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl flex items-center justify-center mb-8 rotate-12">
              <i className="fas fa-check text-white text-3xl -rotate-12"></i>
            </div>
            <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight">You're all set!</h2>
            <p className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto">
              We can't wait to see what you build. If you have any questions, please consult the Swagger documentation or reach out to your administrator.
            </p>
            
            <div className="bg-gray-900 p-12 rounded-[3rem] border border-gray-800">
              <p className="text-gray-400 italic mb-6">
                "Building systems that last is about more than just code; it's about empowering the developers who use them."
              </p>
              <div className="flex flex-col items-center">
                <img 
                  src="/logo.png" 
                  alt="Aura Group Logo" 
                  className="w-16 h-16 object-contain mb-4"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/assets/images/aura-group-logo.png';
                  }}
                />
                <p className="text-white font-bold text-lg">Aura Group Team</p>
                <p className="text-indigo-400 text-sm uppercase tracking-widest font-bold">Master Developer</p>
              </div>
            </div>
            
            <p className="mt-12 text-gray-600 text-sm">
              &copy; 2026 Aura Group Data Management System. All rights reserved.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default DeveloperPortal;
